import React from 'react';

import * as styles from './styles';

import type { UUID, Vehicle } from '@cobliteam/common-types';

import { isWebDataError, isWebDataSuccess, type ReminderPrefill, type UpcomingMaintenance, type UpcomingMaintenanceByVehicleId, type WebData } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import {
  DropdownMenu,
  type DropdownOption,
  Heading1,
  NoConnectionIcon,
  Placeholder,
  PlaceholderNotificationsIcon,
  PlaceholderPastMaintIcon,
  PlaceholderRelaxIcon,
} from '@cobliteam/base-components';

import { Button } from '@cobliteam/ui/components';

import { NotificationsRounded, SettingsRounded } from '@mui/icons-material';

import OdometerWarningBanner from './components/OdometerWarningBanner/OdometerWarningBanner';
import { ReminderLoading } from './components/ReminderLoading/ReminderLoading';
import { VehicleReminders } from './components/VehicleReminders/VehicleReminders';

export interface MaintenanceRemindersProps {
  nextMaintenancesWebData: WebData<UpcomingMaintenanceByVehicleId>;
  futureMaintenancesWebData: WebData<UpcomingMaintenanceByVehicleId>;
  vehiclesWebData: WebData<Vehicle[]>;
  onEditRemindersClick: () => void;
  onDeleteRemindersClick: () => void;
  onRegisterReminderClick: () => void;
  onMarkAsDoneClick: (prefill: ReminderPrefill) => void;
  onLoadPageContent: () => void;
}

type SettingsMenuOption = 'edit' | 'delete';

export const MaintenanceReminders: React.FC<MaintenanceRemindersProps> = ({
  nextMaintenancesWebData,
  futureMaintenancesWebData,
  vehiclesWebData,
  onEditRemindersClick,
  onDeleteRemindersClick,
  onRegisterReminderClick,
  onMarkAsDoneClick,
  onLoadPageContent,
}) => {
  const settingsMenuOptions = [
    {
      key: 'edit' as SettingsMenuOption,
      value: 'Editar lembretes',
    },
    {
      key: 'delete' as SettingsMenuOption,
      value: 'Descadastrar lembretes',
    },
  ];

  React.useEffect(() => {
    Analytics.track('Maintenance Reminders Viewed');
    onLoadPageContent();
  }, []);

  const handleReloadOnError = () => {
    Analytics.track('Maintenance Reminders Reloaded On Error');
    onLoadPageContent();
  };

  const handleRegisterReminder = () => {
    Analytics.track('Maintenance Reminders Register Reminder Button Clicked');
    onRegisterReminderClick();
  };

  const handleMarkAsDone = (vehicleId: UUID, isNextMaintenance: boolean) => {
    Analytics.track('Maintenance Reminders Mark As Done Button Clicked');

    const nextMaintenances = isWebDataSuccess(nextMaintenancesWebData) ? nextMaintenancesWebData.data[vehicleId] || [] : [];
    const futureMaintenances = isWebDataSuccess(futureMaintenancesWebData) ? futureMaintenancesWebData.data[vehicleId] || [] : [];

    const nextRecurrences = nextMaintenances.map((upMaintenance) => ({
      periodicitiesInMeters: upMaintenance.revisionPeriodicityInMeters,
      wasClicked: isNextMaintenance,
      name: upMaintenance.revisionName,
    }));

    const futureRecurrences = futureMaintenances.map((upMaintenance) => ({
      periodicitiesInMeters: upMaintenance.revisionPeriodicityInMeters,
      wasClicked: !isNextMaintenance,
      name: upMaintenance.revisionName,
    }));

    const recurrences = nextRecurrences.concat(futureRecurrences).reduce(
      (recurrenceOutput, recurrence) => {
        recurrenceOutput[recurrence.periodicitiesInMeters] = {
          checked: recurrence.wasClicked,
          name: recurrence.name,
        };
        return recurrenceOutput;
      },
      {} as { [periodicity: string]: { checked: boolean; name?: string } }
    );

    const createdAt = nextMaintenances.length ? nextMaintenances[0].createdAt : futureMaintenances[0].createdAt;

    onMarkAsDoneClick({ vehicleId, recurrences, createdAt });
  };

  const handleSettingsOptionClick = ({ key }: DropdownOption<SettingsMenuOption>) => {
    if (key === 'edit') {
      Analytics.track('Maintenance Reminders Edit Button Clicked');
      onEditRemindersClick();
    } else if (key === 'delete') {
      Analytics.track('Maintenance Reminders Delete Button Clicked');
      onDeleteRemindersClick();
    }
  };

  const hasNoConfiguredReminders = () => {
    const hasNoFuture = isWebDataSuccess(futureMaintenancesWebData) && Object.keys(futureMaintenancesWebData.data).length === 0;

    const hasNoNext = isWebDataSuccess(nextMaintenancesWebData) && Object.keys(nextMaintenancesWebData.data).length === 0;

    return hasNoFuture && hasNoNext;
  };

  const compareMaintenancesInfo = (miA: UpcomingMaintenance[], miB: UpcomingMaintenance[]) => {
    const delayForA = miA[0].revisionPeriodicityInMeters - miA[0].metersDrivenSinceLastRevision;
    const delayForB = miB[0].revisionPeriodicityInMeters - miB[0].metersDrivenSinceLastRevision;

    return delayForA < delayForB ? -1 : delayForA > delayForB ? 1 : 0;
  };

  const getVehiclesWithReminders = (vehicles: Vehicle[], upcomingByVehicle: UpcomingMaintenanceByVehicleId) => {
    return vehicles
      .filter((vehicle) => upcomingByVehicle[vehicle.id] && upcomingByVehicle[vehicle.id].length > 0)
      .map((vehicle) => ({
        vehicle,
        reminders: upcomingByVehicle[vehicle.id],
      }))
      .sort(({ reminders: remindersA }, { reminders: remindersB }) => compareMaintenancesInfo(remindersA, remindersB));
  };

  const renderErrorPlaceholder = () => (
    <Placeholder className={styles.placeholderWithTextButton} text='Ops! Ocorreu um erro no sistema.' Icon={NoConnectionIcon}>
      <Button variant='text' onClick={handleReloadOnError}>
        Recarregar
      </Button>
    </Placeholder>
  );

  const renderNextMaintenances = () => {
    if (isWebDataError(nextMaintenancesWebData) || isWebDataError(vehiclesWebData)) {
      return renderErrorPlaceholder();
    }
    if (isWebDataSuccess(nextMaintenancesWebData) && isWebDataSuccess(vehiclesWebData)) {
      const nextMaintenances = getVehiclesWithReminders(vehiclesWebData.data, nextMaintenancesWebData.data);

      if (nextMaintenances.length === 0) {
        return (
          <Placeholder
            className={styles.placeholderWithTextButton}
            text='Seus veículos ainda não possuem manutenções próximas de vencer. Aproveite seu dia!'
            Icon={PlaceholderRelaxIcon}
          />
        );
      }
      return nextMaintenances.map(({ vehicle, reminders }, index) => (
        <VehicleReminders
          key={`next-${vehicle.id}`}
          vehicle={vehicle}
          addTitles={index === 0}
          upcomingMaintenances={reminders}
          onMarkAsDoneClick={() => handleMarkAsDone(vehicle.id, true)}
        />
      ));
    }
    return <ReminderLoading />;
  };

  const renderFutureMaintenances = () => {
    if (isWebDataError(futureMaintenancesWebData) || isWebDataError(vehiclesWebData)) {
      return renderErrorPlaceholder();
    }
    if (isWebDataSuccess(futureMaintenancesWebData) && isWebDataSuccess(vehiclesWebData)) {
      const futureMaintenances = getVehiclesWithReminders(vehiclesWebData.data, futureMaintenancesWebData.data);

      if (futureMaintenances.length === 0) {
        return (
          <Placeholder
            className={styles.placeholderWithTextButton}
            text={`Não existem manutenções futuras programadas.
              Programe lembretes e tenha maior controle das futuras manutenções dos veículos!`}
            Icon={PlaceholderNotificationsIcon}
          />
        );
      }
      return futureMaintenances.map(({ vehicle, reminders }, index) => (
        <VehicleReminders
          key={`future-${vehicle.id}`}
          vehicle={vehicle}
          addTitles={index === 0}
          upcomingMaintenances={reminders}
          onMarkAsDoneClick={() => handleMarkAsDone(vehicle.id, false)}
        />
      ));
    }
    return <ReminderLoading />;
  };

  return hasNoConfiguredReminders() ? (
    <Placeholder
      className={styles.placeholderWithTextButton}
      text='Configure os seus veículos para começar a receber lembretes de próximas manutenções.'
      Icon={PlaceholderPastMaintIcon}
    >
      <Button variant='text' onClick={handleRegisterReminder}>
        Comece a usar
      </Button>
    </Placeholder>
  ) : (
    <>
      <div className={styles.header}>
        <Heading1>Manutenções próximas</Heading1>
        <div className={styles.actionButtons}>
          <DropdownMenu options={settingsMenuOptions} onClick={handleSettingsOptionClick}>
            <Button color='secondary' size='condensed' startIcon={<SettingsRounded />}>
              Configurar
            </Button>
          </DropdownMenu>

          <Button size='condensed' onClick={handleRegisterReminder} startIcon={<NotificationsRounded />}>
            Programar lembretes
          </Button>
        </div>
      </div>

      <OdometerWarningBanner />

      {renderNextMaintenances()}

      <div className={styles.futureHeader}>
        <Heading1>Manutenções futuras</Heading1>
      </div>
      {renderFutureMaintenances()}
    </>
  );
};
